import { QueryClient } from '@tanstack/react-query'
import { ApiRequestError } from '@/core/api/base.api.client'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The catalog is static per deploy; connections change only when the user
      // acts. A minute of staleness avoids refetch storms while navigating.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 4xx just repeats the same rejection. Only transient
        // failures are worth a second attempt.
        if (error instanceof ApiRequestError && !error.retryable) return false
        return failureCount < 2
      },
    },
    mutations: {
      // Running an action is not idempotent — never retry it automatically.
      retry: false,
    },
  },
})

export const queryKeys = {
  whoami: ['whoami'] as const,
  catalog: (params?: { category?: string; q?: string }) => ['catalog', params ?? {}] as const,
  connector: (id: string) => ['connector', id] as const,
  actionSchema: (connectorId: string, actionId: string) =>
    ['actionSchema', connectorId, actionId] as const,
  connections: (connectorId?: string) => ['connections', connectorId ?? 'all'] as const,
  connection: (id: string) => ['connection', id] as const,
  health: (id: string) => ['health', id] as const,
  availableActions: (connectionId: string) => ['availableActions', connectionId] as const,
  lookup: (connectionId: string, actionId: string, deps: unknown) =>
    ['lookup', connectionId, actionId, deps] as const,
  executions: (filters: unknown) => ['executions', filters] as const,
  execution: (id: string) => ['execution', id] as const,
}

/** Called after any connect/revoke so every connection-shaped view refreshes. */
export function invalidateConnections() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['connections'] }),
    queryClient.invalidateQueries({ queryKey: ['connection'] }),
  ])
}
